import React, { useEffect, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  TextField,
  Grid as Grid2,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  colors,
  Divider,
  CircularProgress,
  Typography,
  Paper,
  Checkbox,
  FormGroup,
  FormControlLabel,
  InputAdornment,
  Avatar,
} from "@mui/material";
import { FiSearch as SearchIcon } from "react-icons/fi";
import {
  useGetRoleListQuery,
  useLazyGetRoleListQuery,
} from "../../../../Services/RolesService";
import {
  useCreateAndUpdateEmployeeMutation,
  useLazyGetAllCountryQuery,
  useLazyGetemployeecheckmobileQuery,
} from "../../../../Services/EmployeeService";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../Redux/Slices/NotificationSlice";
import Breadcrumb from "../../../../Components/BreadCrumb";
import Utils from "../../../../Helpers/Utils";
import { useParams, useNavigate } from "react-router-dom";
import { useLazyGetEmployeeByIdQuery } from "../../../../Services/EmployeeService";
import { useLazyGetRolePermissionByRoleIdQuery } from "../../../../Services/RolesService";
import { CountryItemModel } from "../../../../Helpers/Interfaces/SettingType";

interface EmployeeFormInputs {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  countryDialCode: string;
  empId: string;
  roleId: string;
  _id?: string;
  experience: string;
  seniorityLevel: string;
  employmentType: string;
  salary: string;
  isActive: boolean;
  permissions: Record<string, Set<string>>;
  individualPermissions: Record<string, Set<string>>;
}

interface ApiErrorResponse {
  status: string;
  statusCode: number;
  message: string;
}

/** Available permissions configuration */
const permissions = Utils.permissions;
// console.log(permissions, "permissions");

/** Available actions for permissions */
const actions = ["view", "edit"];

const AddEmployee: React.FC<any> = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [status, setStatus] = useState<
    "success" | "error" | "warning" | "info"
  >("success");
  const [triggerRoleList, { data: roleList, error: roleError }] =
    useLazyGetRoleListQuery();
  const [
    createAndUpdateEmployee,
    { isLoading, isSuccess, error: createError, isError },
  ] = useCreateAndUpdateEmployeeMutation();
  const [selectAll, setSelectAll] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<
    Record<string, Set<string>>
  >({});
  const [lockedPermissions, setLockedPermissions] = useState<
    Record<string, Set<string>>
  >({});

  const [countries, setCountries] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<any>(null); // or a default country
  const [countrySearch, setCountrySearch] = useState("");
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const filteredCountries = countries.filter((country) =>
    (country?.name?.common || "")
      .toLowerCase()
      .includes(countrySearch.toLowerCase())
  );

  const [getAllCountryTrigger, { data: countriesData }] =
    useLazyGetAllCountryQuery();

  const navigate = useNavigate();
  const { id } = useParams();
  const isAddMode = !id;

  const [triggerGetEmployee, { data: employeeInfo }] =
    useLazyGetEmployeeByIdQuery();
  const [trigger] = useLazyGetRolePermissionByRoleIdQuery();

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
    watch,
  } = useForm<EmployeeFormInputs>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
      countryDialCode: "",
      empId: "",
      roleId: "",
      experience: "",
      seniorityLevel: "Fresher",
      employmentType: "Full Time",
      salary: "",
      isActive: true,
      permissions: {},
    },
    mode: "onChange",
  });

  const roleId = watch("roleId"); // get the current roleId

  const watchedMobile = useWatch({ control, name: "mobile" });
  const watchedDialCode = useWatch({ control, name: "countryDialCode" });

  const [triggerCheckMobile] = useLazyGetemployeecheckmobileQuery();

  useEffect(() => {
    getAllCountryTrigger({
      filters: [{ key: "isDelete", value: false }],
    });
  }, []);

  useEffect(() => {
    if (countriesData?.data) {
      // Sort alphabetically by country name for a usable Country Code dropdown
      // (the API returns them in DB insertion order, not alphabetical).
      setCountries(
        [...countriesData.data].sort((a: any, b: any) =>
          (a?.name?.common || "").localeCompare(b?.name?.common || "")
        )
      );

      if (employeeInfo?.countryDialCode) {
        const matchedCountry = countriesData.data.find(
          (country: any) => country.dailCode === employeeInfo.countryDialCode
        );
        if (matchedCountry) {
          setSelectedCountry(matchedCountry);
        }
      }
    }
  }, [countriesData, employeeInfo]);

  useEffect(() => {
    const checkMobile = async () => {
      if (!watchedMobile || watchedMobile.length < 9 || !watchedDialCode)
        return;

      const currentFullMobile = `${watchedDialCode}${watchedMobile}`;
      const originalFullMobile = employeeInfo?.mobile?.replace(/\D/g, ""); // remove + or non-digits for comparison
      const currentFullMobileNormalized = currentFullMobile.replace(/\D/g, "");

      // Only proceed if the number has changed
      if (originalFullMobile === currentFullMobileNormalized) return;

      try {
        const result = await triggerCheckMobile({
          mobile: currentFullMobile,
          countryDialCode: watchedDialCode,
        }).unwrap();

        if (result?.exists) {
          setError("mobile", {
            type: "manual",
            message: "Mobile number already exists",
          });
        } else {
          clearErrors("mobile");
        }
      } catch (err: any) {
        setError("mobile", {
          type: "manual",
          message: err.data.message,
        });
      }
    };

    checkMobile();
  }, [watchedMobile, watchedDialCode, employeeInfo]);

  useEffect(() => {
    triggerRoleList({ page: 1, pageSize: 101 });
    if (id) {
      triggerGetEmployee(id);
    }
  }, [id]);

  useEffect(() => {
    if (employeeInfo?.mobile && employeeInfo?.countryDialCode) {
      const rawMobile = employeeInfo.mobile.toString();

      // Remove country dial code (e.g., '+91' or '91')
      const dialCode = employeeInfo.countryDialCode.replace("+", "");
      let mobileWithoutDialCode = rawMobile;

      // Remove '+' and dialCode if mobile starts with it
      if (rawMobile.startsWith("+" + dialCode)) {
        mobileWithoutDialCode = rawMobile.slice(dialCode.length + 1);
      } else if (rawMobile.startsWith(dialCode)) {
        mobileWithoutDialCode = rawMobile.slice(dialCode.length);
      }

      setValue("mobile", mobileWithoutDialCode);
    }
  }, [employeeInfo, setValue]);

  useEffect(() => {
    if (employeeInfo) {
      // console.log(employeeInfo, "employee info ");
      setValue("firstName", employeeInfo?.firstName);
      setValue("lastName", employeeInfo?.lastName);
      setValue("email", employeeInfo?.email);
      // setValue('mobile', employeeInfo?.mobile);
      setValue("countryDialCode", employeeInfo?.countryDialCode);
      setValue("empId", employeeInfo?.empId);
      setValue("roleId", employeeInfo?.roleId || "");
      setValue("_id", id);
      setValue("experience", employeeInfo?.experience || "");
      setValue("seniorityLevel", employeeInfo?.seniorityLevel || "Fresher");
      setValue("employmentType", employeeInfo?.employmentType || "Full Time");
      setValue("salary", employeeInfo?.salary || "");
      setValue("isActive", employeeInfo?.isActive ?? true);
      // setValue('permissions', employeeInfo?.permissions || {});

      if (employeeInfo?.permissions) {
        // console.log(
        //   employeeInfo.permissions,
        //   "employeeInfo.permissions",
        //   permissions
        // );

        setRoleData(employeeInfo);
        const convertedPermissions = Object.fromEntries(
          Object.entries(
            Utils.reverseToArrayFormatRole(employeeInfo)?.permissions
          ).map(([key, actions]) => [key, new Set(actions as string[])])
        );
        setLockedPermissions(convertedPermissions);
        // setRoleData(employeeInfo);

        // setSelectedPermissions(employeeInfo.individualPermissions);
      }
    }
  }, [employeeInfo]);

  /**
   * Fetches and sets role data for editing
   * @param roleId - The ID of the role to fetch
   */
  const setRoleData = async (result: any) => {
    if (result && result?._id) {
      /** Convert permissions from array format to Set format */
      const formattedPermissions = Utils.reverseToArrayFormatRole({
        ...result,
      }).individualPermissions;
      /** Convert permissions to Set format */
      const permissionsWithSets = Object.fromEntries(
        Object.entries(formattedPermissions).map(([key, actions]: any) => [
          key,
          new Set(actions),
        ])
      );

      /** Update states */
      setSelectedPermissions(permissionsWithSets);
      // setValue("permissions", formattedPermissions);

      /** Update selectAll state based on permissions */
      const allSelected = Object.values(permissionsWithSets).every(
        (set: any) =>
          actions.length === set.size &&
          actions.every((action) => set.has(action.toLowerCase()))
      );
      setSelectAll(allSelected);
    }
  };

  const onInvalid = (formErrors: any) => {
    const fieldOrder: Array<keyof EmployeeFormInputs | "countryDialCode"> = [
      "empId",
      "firstName",
      "lastName",
      "email",
      "countryDialCode",
      "mobile",
      "roleId",
      "experience",
      "seniorityLevel",
    ];

    const labelMap: Record<string, string> = {
      empId: "Employee ID",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      countryDialCode: "Country Code",
      mobile: "Mobile",
      roleId: "Role",
      experience: "Experience",
      seniorityLevel: "Seniority Level",
    };

    let firstKey = fieldOrder.find((k) => formErrors?.[k]);
    if (!firstKey) {
      const keys = Object.keys(formErrors);
      firstKey = (keys[0] as any) || undefined;
    }

    if (firstKey) {
      const message = formErrors[firstKey]?.message || `${labelMap[firstKey as string] || firstKey} is required`;
      dispatch(
        showSnackbar({
          message,
          severity: "warning",
        })
      );
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectAll(checked);

    const newPermissions: Record<string, Set<string>> = {};

    permissions.forEach((permission) => {
      const key = permission.id;

      // Always start from default
      const defaultSet = lockedPermissions[key] || new Set();

      if (checked) {
        // Add all actions + retain defaults
        newPermissions[key] = new Set([
          ...Array.from(defaultSet),
          ...actions.map((a) => a.toLowerCase()),
        ]);
      } else {
        // Keep only default permissions when unchecking Select All
        newPermissions[key] = new Set(defaultSet);
      }
    });

    setSelectedPermissions(newPermissions);
    setValue("permissions", newPermissions);
  };

  const handlePermissionChange = (
    permissionId: string,
    type: string,
    checked: boolean
  ) => {
    setSelectedPermissions((prev) => {
      const newPermissions = { ...prev };
      if (!newPermissions[permissionId]) {
        newPermissions[permissionId] = new Set();
      }
      const permissionSet = new Set(newPermissions[permissionId]);


      /** Add or remove permission based on checkbox state */
      if (checked) {
        // If "none" is being selected, clear others
        if (type === "none") {
          permissionSet.clear();
          // permissionSet.add("none");
        } else {
          // If "view" or "edit" is selected, remove "none"
          // permissionSet.delete("none");
          permissionSet.add(type);
        }
      } else {
        permissionSet.delete(type);
      }
      newPermissions[permissionId] = permissionSet;

      /** Update selectAll state based on all permissions */
      const allSelected = Object.values(newPermissions).every(
        (set) =>
          actions.length === set.size &&
          actions.every((action) => set.has(action.toLowerCase()))
      );
      setSelectAll(allSelected);

      setValue("individualPermissions", newPermissions);
      return newPermissions;
    });
  };

  const onSubmit = async (data: EmployeeFormInputs) => {
    /*
     * A template string turns a missing value into the WORD "undefined".
     *
     * This was `${data.countryDialCode}${data.mobile}`, and neither part
     * is required. Leave the phone blank and the employee was saved with
     * the string "undefined" as their number; fill in the number but not
     * the dialling code and you got "undefined9876543211", which is a
     * real number nobody can ring. Three employees are stored that way.
     *
     * The profile screen was reading it correctly -- `mobile || "N/A"` --
     * and printing "undefined" because that is genuinely what is in the
     * database. Fixing the display would only have hidden it.
     *
     * Built from the parts that exist. Nothing to join means no mobile,
     * which the field already allows.
     */
    const dialCode = String(data.countryDialCode ?? "").trim();
    const number = String(data.mobile ?? "").trim();
    const mobile = number ? `${dialCode}${number}` : "";

    const payload = {
      ...data,
      mobile,
    };

    // Convert Set objects to arrays in permissions
    const formattedData = {
      ...payload,
      individualPermissions: Object.entries(selectedPermissions).reduce(
        (acc, [key, value]) => {
          if (value.size > 0) {
            acc[key] = Array.from(value);
          }
          return acc;
        },
        {} as Record<string, string[]>
      ),
      permissions: Object.entries(lockedPermissions).reduce(
        (acc, [key, value]) => {
          if (value.size > 0) {
            acc[key] = Array.from(value);
          }
          return acc;
        },
        {} as Record<string, string[]>
      ),
    };

    try {
      if (id) {
        await createAndUpdateEmployee({
          id: id,
          data: Utils.convertToBooleanFormatRole(formattedData),
        }).unwrap();
      } else {
        // Create new employee
        await createAndUpdateEmployee(
          Utils.convertToBooleanFormatRole(formattedData)
        ).unwrap();
      }
    } catch (err) {
      dispatch(
        showSnackbar({
          message: getMessage(err),
          severity: "error",
        })
      );
    }
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        showSnackbar({
          message: id
            ? "Employee updated successfully!"
            : "Employee created successfully!",
          severity: "success",
        })
      );
      onClose();
    }
  }, [isSuccess]);

  const getMessage = (error: any) => {
    return error?.data?.message;
  };

  const onClose = () => {
    navigate("/employee");
  };

  const breadcrumbItems = [
    { label: "Employee List", path: "employee" },
    { label: id ? "Edit Employee" : "Add Employee" },
  ];


  return (
    <Box
      sx={{ display: "flex", flex: 1, flexDirection: "column", height: "100%" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div style={{ flex: 1 }}>
          <Breadcrumb items={breadcrumbItems} name={id ? "Edit Employee" : "Add Employee"} />
        </div>
      </div>

      <Box className={""}>
        <Paper>
          <form
            onSubmit={handleSubmit(onSubmit, onInvalid)}
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
            autoComplete={"off"}
          >
            <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 3 }}>
              <Grid2 container spacing={3}>
                <Grid2 size={{ sm: 12, md: 12, lg: 4 }}>
                  <Controller
                    name="empId"
                    control={control}
                    rules={{
                      required: "Employee ID is required",
                      pattern: {
                        value: /^[a-zA-Z0-9-_]+$/,
                        message:
                          "Employee ID can only contain letters, numbers, hyphens, and underscores",
                      },
                      maxLength: {
                        value: 15,
                        message: "Employee ID max should be 15 characters",
                      },
                      minLength: {
                        value: 3,
                        message: "Employee ID must be at least 3 characters",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Employee ID"
                        error={!!errors.empId}
                        helperText={errors.empId?.message}
                        size="small"
                        placeholder="Enter employee ID"
                        disabled={!!id}
                        InputProps={{
                          style: id ? { backgroundColor: '#f5f5f5' } : {}
                        }}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ sm: 12, lg: 4 }}>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{
                      required: "First name is required",
                      validate: (value) => value.trim() !== "" || "First name cannot be empty or whitespace"

                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="First Name"
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        size="small"
                        placeholder="Enter first name"
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ sm: 12, lg: 4 }}>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{
                      required: "Last name is required",
                      validate: (value) => value.trim() !== "" || "Last name cannot be empty or whitespace"
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Last Name"
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        size="small"
                        placeholder="Enter last name"
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ sm: 12, lg: 4 }}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                      maxLength: {
                        value: 50,
                        message: "Email max should be 50 characters",
                      },
                      validate: {
                        // notAdmin: (value) =>
                        //   !value.toLowerCase().includes('admin@') ||
                        //   'Email cannot contain "admin@"',
                        validDomain: (value) =>
                          /\.(com|net|org|edu|gov|in)$/i.test(value) ||
                          "Invalid email domain",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email"
                        type="email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        size="small"
                        placeholder="Enter email address"
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ sm: 12, lg: 4 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Select
                      size="small"
                      value={selectedCountry?._id || ""}
                      open={countryDropdownOpen}
                      onOpen={() => setCountryDropdownOpen(true)}
                      onClose={() => {
                        setCountryDropdownOpen(false);
                        setCountrySearch("");
                      }}
                      onChange={(e) => {
                        const country = countries.find(
                          (c) => c._id === e.target.value
                        );
                        setSelectedCountry(country);
                        setValue("countryDialCode", country?.dailCode || "");
                      }}
                      displayEmpty
                      renderValue={() => (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                          }}
                        >
                          <Avatar
                            src={selectedCountry?.flags?.png || "https://www.pngfind.com/pngs/m/328-3288904_tel-png-icon-land-phone-logo-png-transparent.png"}
                            alt="flag"
                            sx={{ width: 20, height: 20 }}
                          />
                          <span>{selectedCountry?.dailCode || ""}</span>
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 300, width: 260 },
                        },
                      }}
                      sx={{
                        minWidth: 80,
                        "& .MuiOutlinedInput-notchedOutline": {
                          paddingRight: 0,
                        },
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          paddingRight: "32px",
                        },
                        "& .MuiSelect-icon": {
                          display: "none",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          p: 0.5,
                          position: "sticky",
                          top: 0,
                          backgroundColor: "white",
                          zIndex: 1,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search country..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          sx={{
                            "& .MuiInputBase-root": {
                              height: 30,
                              fontSize: 12,
                            },
                            "& .MuiInputBase-input": {
                              padding: "4px 4px",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ pl: 0.5 }}>
                                <SearchIcon size={13} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      {filteredCountries.length === 0 ? (
                        <MenuItem disabled value="">
                          No matching countries
                        </MenuItem>
                      ) : (
                        filteredCountries.map((country) => (
                          <MenuItem key={country._id} value={country._id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: "100%",
                                overflow: "hidden",
                              }}
                            >
                              <Avatar
                                src={country.flags?.png}
                                alt="flag"
                                sx={{ width: 20, height: 20, flexShrink: 0 }}
                              />
                              <Box
                                component="span"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {country.name.common} ({country.dailCode})
                              </Box>
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>

                    <Controller
                      name="mobile"
                      control={control}
                      rules={{
                        required: "Mobile number is required",
                        pattern: {
                          value: /^[0-9]{9,13}$/,
                          message:
                            "Mobile number must be exactly 9 to 13 digits",
                        },
                      }}
                      disabled={!selectedCountry}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Mobile"
                          type="number"
                          fullWidth
                          error={!!errors.mobile}
                          helperText={errors.mobile?.message}
                          size="small"
                          placeholder="Enter mobile number"
                          inputProps={{ maxLength: 13, minLength: 9 }}
                        />
                      )}
                    />
                  </Box>
                </Grid2>

                <Grid2 size={{ sm: 12, lg: 4 }}>
                  <Controller
                    name="roleId"
                    control={control}
                    rules={{ required: "Role is required" }}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        size="small"
                        error={!!errors.roleId}
                      >
                        <InputLabel>Role</InputLabel>
                        <Select
                          {...field}
                          value={field.value || ""}
                          label="Role"
                          onChange={async (e) => {
                            const selectedRoleId = e.target.value;
                            field.onChange(selectedRoleId); // Update the form state
                            if (selectedRoleId) {
                              const result: any = await trigger(
                                selectedRoleId
                              ).unwrap();
                              if (result) {
                                const convertedPermissions = Object.fromEntries(
                                  Object.entries(
                                    Utils.reverseToArrayFormatRole(result)
                                      .permissions
                                  ).map(([key, actions]) => [
                                    key,
                                    new Set(actions as string[]),
                                  ])
                                );
                                setSelectedPermissions(convertedPermissions);
                                setLockedPermissions(convertedPermissions);
                              }
                            }
                          }}
                          sx={{
                            height: "45px",
                            fontSize: "12px",
                            "& .MuiSelect-select": {
                              paddingTop: "10px",
                              paddingBottom: "10px",
                            },
                          }}
                        >
                          <MenuItem value="" sx={{ fontSize: "12px" }}>
                            <em>Select Role</em>
                          </MenuItem>
                          {roleList &&
                            roleList?.data &&
                            roleList?.data?.map((item: any) => (
                              <MenuItem
                                key={item._id}
                                value={item._id}
                                sx={{
                                  fontSize: "12px",
                                  display:
                                    item.roleName === "Super Admin"
                                      ? "none"
                                      : "block",
                                }}
                              >
                                {item.roleName}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid2>

                <Grid2 size={{ sm: 12, lg: 4 }}>
                  {/* <Controller
                    name="experience"
                    control={control}
                    rules={{
                      pattern: {
                        value: /^\d*\.?\d{0,1}$/,
                        message: "Please enter valid experience (e.g., 4.5)",
                      },
                      min: {
                        value: 0,
                        message:
                          "Experience should be greater than or equal to 0",
                      },
                      max: {
                        value: 70,
                        message:
                          "Experience should be less than or equal to 70",
                      },
                      maxLength: {
                        value: 2,
                        message: "Experience max should be 2 characters",
                      },
                      validate: (value) => {
                        const num = parseFloat(value);
                        if (isNaN(num)) return "Please enter a valid number";
                        if (num < 0) return "Experience cannot be negative";
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Experience(in years)"
                        error={!!errors.experience}
                        helperText={errors.experience?.message}
                        size="small"
                        type="number"
                        placeholder="Enter experience (e.g., 4.5)"
                        inputProps={{
                          step: "0.1",
                        }}
                      />
                    )}
                  /> */}
                  <Controller
                    name="experience"
                    control={control}
                    rules={{
                      required: "Experience is required",
                      validate: (value) => {
                        const num = parseFloat(value);

                        if (isNaN(num)) return "Enter a valid number";
                        if (num < 0 || num > 70) return "Experience must be between 0 and 70";
                        if (!/^\d{1,2}(\.\d{1})?$/.test(value)) {
                          return "Only one digit allowed after decimal (e.g., 4.5)";
                        }

                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Experience (in years)"
                        error={!!errors.experience}
                        helperText={errors.experience?.message}
                        size="small"
                        type="number"
                        placeholder="Enter experience (e.g., 4.5)"
                        inputProps={{
                          step: "0.1",
                        }}
                      />
                    )}
                  />

                </Grid2>

                <Grid2 size={{ sm: 12, lg: 4 }}>
                  <Controller
                    name="seniorityLevel"
                    control={control}
                    rules={{ required: "Seniority level is required" }}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        size="small"
                        error={!!errors.seniorityLevel}
                      >
                        <InputLabel>Seniority Level</InputLabel>
                        <Select
                          {...field}
                          label="Seniority Level"
                          sx={{
                            height: "45px",
                            fontSize: "12px",
                            "& .MuiSelect-select": {
                              paddingTop: "10px",
                              paddingBottom: "10px",
                            },
                          }}
                        >
                          <MenuItem
                            value="Senior Level"
                            sx={{ fontSize: "12px" }}
                          >
                            Senior Level
                          </MenuItem>
                          <MenuItem value="Mid Level" sx={{ fontSize: "12px" }}>
                            Mid Level
                          </MenuItem>
                          <MenuItem value="Fresher" sx={{ fontSize: "12px" }}>
                            Fresher
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid2>

                {/* <Grid2 size={{ sm: 12, lg: 4 }}>
                  <Controller
                    name="employmentType"
                    control={control}
                    rules={{ required: 'Employment type is required' }}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        size="small"
                        error={!!errors.employmentType}
                      >
                        <InputLabel>Employment Type <em>(optional)</em></InputLabel>
                        <Select
                          {...field}
                          label="Employment Type"
                          sx={{
                            height: '45px',
                            fontSize: '12px',
                            '& .MuiSelect-select': {
                              paddingTop: '10px',
                              paddingBottom: '10px',
                            }
                          }}
                        >
                          <MenuItem value="Full Time" sx={{ fontSize: '12px' }}>Full Time</MenuItem>
                          <MenuItem value="Part Time" sx={{ fontSize: '12px' }}>Part Time</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid2>

                <Grid2 size={{ sm: 12, lg: 4 }}>
                  <Controller
                    name={'salary'}
                    control={control}
                    rules={{
                      max: {
                        value: 99999999999,
                        message: 'Salary should be less than or equal to 999,999,999,999'
                      },
                      pattern: {
                        value: /^\d+(\.\d{1,2})?\s*(€|rs|RS|Rs)?$/,
                        message: 'Please enter valid salary (e.g., 20€)'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={<span>Salary <em>(optional)</em></span>}
                        error={!!errors.salary}
                        helperText={errors.salary?.message}
                        size="small"
                        type="number"
                        placeholder="Enter salary (e.g., 20€)"
                        slotProps={{
                          input: { endAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }
                        }}
                      />
                    )}
                  />
                </Grid2> */}

                <Grid2 size={{ sm: 12, lg: 4 }}>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={value}
                          onChange={(e) => onChange(e.target.value === "true")}
                          label="Status"
                          sx={{
                            height: "45px",
                            fontSize: "12px",
                            "& .MuiSelect-select": {
                              paddingTop: "10px",
                              paddingBottom: "10px",
                            },
                          }}
                        >
                          <MenuItem value={"true"} sx={{ fontSize: "12px" }}>
                            Active
                          </MenuItem>
                          <MenuItem value={"false"} sx={{ fontSize: "12px" }}>
                            Inactive
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, lg: 12 }}>
                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 6,
                      overflow: "hidden",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.grey[100],
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "medium" }}
                      >
                        Role Permissions
                      </Typography>

                      <FormControlLabel
                        sx={{ visibility: isAddMode ? "hidden" : "visible" }}
                        control={
                          <Checkbox
                            checked={selectAll}
                            onChange={handleSelectAll}
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <span style={{ fontSize: "0.8.5rem" }}>
                            Select All
                          </span>
                        }
                      />
                    </Box>
                    <Box sx={{ width: "100%" }}>
                      {permissions.map((permission) => (
                        <Box
                          key={permission.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            py: 0.8,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            "&:last-child": {
                              borderBottom: "none",
                            },
                            width: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              flex: 1,
                              px: 2,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {permission.label}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 3,
                              justifyContent: "flex-end",
                              minWidth: "300px",
                              marginRight: 1,
                            }}
                          >
                            {actions.map((action) => {
                              const actionKey = action.toLowerCase();
                              const isChecked =
                                selectedPermissions[permission.id]?.has(
                                  actionKey
                                ) || false;
                              const isLocked =
                                lockedPermissions[permission.id]?.has(
                                  actionKey
                                ) || false;
                              return (
                                <FormControlLabel
                                  key={`${permission.id}-${action}`}
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={(e) => {
                                        handlePermissionChange(
                                          permission.id,
                                          action,
                                          e.target.checked
                                        )
                                      }}
                                      color="primary"
                                      size="small"
                                      disabled={isLocked}
                                    />
                                  }
                                  label={action.charAt(0).toUpperCase() + action.slice(1).toLowerCase()}
                                  sx={{
                                    mr: 0,
                                    "& .MuiTypography-root": {
                                      fontSize: "0.875rem",
                                      color: theme.palette.text.secondary,
                                    },
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  {errors.permissions && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {(errors.permissions as { message: string })?.message ||
                        "Please select at least one permission"}
                    </Typography>
                  )}
                </Grid2>
              </Grid2>
            </Box>



            {/* Footer */}
            <Box
              id="footer"
              sx={{
                mt: "auto",
                display: "flex",
                gap: 2,
                py: 1,
                px: 2,
                backgroundColor: colors.common.white,
                borderTop: `1px solid ${theme.palette.divider}`,
                justifyContent: "center",
              }}
            >
              <Button
                onClick={onClose}
                fullWidth
                variant="outlined"
                color="error"
                sx={{ width: 100 }}
              >
                Cancel
              </Button>

              <Divider orientation="vertical" flexItem />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                color="primary"
                disabled={isLoading}
                sx={{ width: 170 }}
              >
                {isLoading ? (
                  <CircularProgress color={"error"} size={16} />
                ) : id ? (
                  "Update Employee"
                ) : (
                  "Add Employee"
                )}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
};

export default AddEmployee;
